import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Err } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { DataError } from 'app/common/application'
import { Form, PagedTable, SearchInput } from 'app/common/presentation'
import { DeleteIcon, PlusIcon } from 'app/common/presentation/icons'
import { useWorkspace } from 'app/user/application'
import { translateStatus } from 'app/user/presentation'
import { WorkspaceUser, User } from 'app/user/types'

import { Button, IconButton, Status, Table } from 'components'

import InviteModal from './InviteModal'
import styles from './Users.module.scss'

const UsersSetings = () => {
  const { t } = useTranslation('settings')
  const { createNotification } = useNotifications()
  const { getWorkspaceUsers, inviteUser } = useWorkspace()

  const [search, setSearch] = useState('')
  const [users, setUsers] = useState<WorkspaceUser[]>([])
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false)
  const [inviteUserError, setInviteUserError] = useState<string>()

  const filteredUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          user.email.toLowerCase().includes(search.toLowerCase()) ||
          user.firstName.toLowerCase().includes(search.toLowerCase()) ||
          user.lastName.toLowerCase().includes(search.toLowerCase()),
      ),
    [users, search],
  )

  const loadWorkspaceUser = useCallback(
    () =>
      getWorkspaceUsers().then((result) => {
        if (result.ok) {
          setUsers(result.val)
        } else {
          createNotification({
            message: result.val.message,
            title: t(`http.${result.val.code}`),
            type: 'error',
          })
        }
      }),
    [createNotification, getWorkspaceUsers, t],
  )

  useEffect(() => void loadWorkspaceUser(), [loadWorkspaceUser])

  useEffect(() => setInviteUserError(undefined), [inviteUserModalOpen])

  const handleInviteUser = (emails: string) => {
    setInviteUserError(undefined)

    void Promise.allSettled(
      emails
        .split(',')
        .filter(Boolean)
        .map((email) => email.trim())
        .map((email) => inviteUser(email)),
    ).then((results) => {
      void loadWorkspaceUser()

      const errorResult = results.find(
        (result): result is PromiseFulfilledResult<Err<DataError<number>>> =>
          result.status === 'fulfilled' && !result.value.ok,
      )?.value.val
      if (errorResult) {
        setInviteUserError(errorResult.message)
      } else {
        setInviteUserModalOpen(false)
      }
    })
  }

  const handleDelete = (user: User) => (e: SyntheticEvent) => {
    e.stopPropagation()

    createNotification({
      message: 'What to do here?',
      title: `Delete ${user.email}`,
      type: 'info',
    })
  }

  return (
    <div className={styles.users}>
      <Form.FormRow
        className={styles.usersFormRow}
        description={t('users.users.description')}
        title={t('users.users.title')}
      >
        <>
          <SearchInput
            className={styles.search}
            onChange={setSearch}
            placeholder={t('users.search')}
            value={search}
          />
          <Button
            className={styles.invite}
            onClick={() => setInviteUserModalOpen(true)}
            rightIcon={<PlusIcon />}
          >
            {t('users.invite.title')}
          </Button>
        </>
      </Form.FormRow>
      <PagedTable
        className={styles.table}
        columns={[t('users.columns.name'), t('users.columns.email'), t('users.columns.status'), '']}
        data={filteredUsers}
        renderRow={(user, index) => (
          <Table.Row key={index}>
            <Table.Cell>
              {user.firstName} {user.lastName}
            </Table.Cell>
            <Table.Cell>{user.email}</Table.Cell>
            <Table.Cell>
              <Status status={translateStatus(user)}>{t(`users.status.${user.status}`)}</Status>
            </Table.Cell>
            <Table.Cell>
              <IconButton
                className={styles.delete}
                icon={DeleteIcon}
                onClick={handleDelete(user)}
                variant="tertiary"
              />
            </Table.Cell>
          </Table.Row>
        )}
      />
      <InviteModal
        onClose={() => setInviteUserModalOpen(false)}
        inviteUser={handleInviteUser}
        inviteUserError={inviteUserError}
        open={inviteUserModalOpen}
      />
    </div>
  )
}

export default UsersSetings
